import axios from "axios";
import {
  getAuthorizationHeaders,
  getBearerToken,
  setBearerToken,
} from "./authToken";

// Store for the auth context reference
let authContextRef = null;

// Function to set the auth context reference
export const setAuthContext = (authContext) => {
  authContextRef = authContext;
};

// Create a function to setup axios interceptors
export const setupAxiosInterceptors = () => {
  // Request interceptor to add authentication token to all requests
  axios.interceptors.request.use(
    (config) => {
      // Skip adding the token for auth/login requests
      if (config.url && (config.url.includes("/auth/login") || config.url.includes("/login"))) {
        return config;
      }
      
      const authHeaders = getAuthorizationHeaders();
      if (authHeaders.Authorization) {
        config.headers = {
          ...config.headers,
          ...authHeaders,
        };
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Check for a new token in response headers from the backend
      const newTokenHeader = response.headers['authorization'] || response.headers['x-access-token'];
      if (newTokenHeader && newTokenHeader.toLowerCase().startsWith('bearer ')) {
        const newToken = newTokenHeader.substring(7); // Remove "Bearer "
        const currentToken = getBearerToken();
        if (newToken && newToken !== currentToken) {
          setBearerToken(newToken);
          console.log("Refreshed token stored in sessionStorage.");
          // Notify AuthContext to update its state if necessary
          if (authContextRef && typeof authContextRef.updateAuthToken === 'function') {
            authContextRef.updateAuthToken(newToken);
          }
        }
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Ignore 401 errors from the login endpoint itself
      if (error.response && 
          error.response.status === 401 && 
          (originalRequest.url.includes("/auth/login") || originalRequest.url.includes("/login"))) {
        return Promise.reject(error);
      }

      // NEW: Attempt to recover from 401/403 if a new token is in the response headers
      //      AND this is the first attempt to recover this request via this specific mechanism.
      if (error.response && (error.response.status === 401 || error.response.status === 403) && !originalRequest._tokenRecoveryViaHeaderAttempted) {
        const newTokenHeader = error.response.headers['authorization'] || error.response.headers['x-access-token'];
        if (newTokenHeader && newTokenHeader.toLowerCase().startsWith('bearer ')) {
          const newToken = newTokenHeader.substring(7);
          // Condition simplified: if a new token is provided in the 401 header, and we have a way to update it, try to recover.
          if (newToken && authContextRef && typeof authContextRef.updateAuthToken === 'function') {
            console.log("axiosConfig: New token found in 401/403 response header. Updating auth state and retrying original request.");
            authContextRef.updateAuthToken(newToken); // This updates sessionStorage and React context
            originalRequest._tokenRecoveryViaHeaderAttempted = true; // Mark that we've tried this specific recovery path
            // The request interceptor will pick up the new token for the retry from sessionStorage.
            return axios(originalRequest); // Retry the original request
          }
        }
      }

      // If the error is not 401/403, OR we've already tried to retry with a token from header, OR no auth context, reject.
      // This means if _tokenRecoveryViaHeaderAttempted was true and the retry also failed (e.g. with another 401, or 500),
      // we will fall through to here. If it was a 401/403 again, the next block will handle it via modal.
      if (
        !error.response ||
        ((error.response.status !== 401 && error.response.status !== 403)) || // It's an error other than 401/403
        !authContextRef // No auth context to proceed with modal logic
      ) {
        if (authContextRef === null && error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.error("Auth context not available for token refresh handling. User may need to be logged out.");
        }
        return Promise.reject(error);
      }

      // Guest public/share pages must never show "Session Expired".
      // Stray 401/403 from authenticated-only APIs should fail quietly there.
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      const isPublicShareSurface =
        path.startsWith("/public/") ||
        path.startsWith("/record/") ||
        path.startsWith("/record-not-public") ||
        path.startsWith("/record-invalid");
      if (isPublicShareSurface) {
        return Promise.reject(error);
      }

      // At this point, it's a 401/403, and either:
      // 1. No new token was found in its headers.
      // 2. Recovery via header token was attempted (_tokenRecoveryViaHeaderAttempted = true) and that retry also failed with 401/403.
      // Now, proceed to modal via handleTokenExpiry.
      // Use originalRequest._retry for the modal path to prevent loops if modal interaction also leads to retries that fail.
      if (originalRequest._retryModalPath) { // If already retried after a handleTokenExpiry modal attempt
        console.warn("axiosConfig: Request failed again after modal-based token expiry handling. Rejecting.", originalRequest.url);
        return Promise.reject(error);
      }
      originalRequest._retryModalPath = true; // Mark this request as having gone through the handleTokenExpiry (modal) path
      
      console.log("axiosConfig: Attempting to show login modal via AuthContext.handleTokenExpiry for request:", originalRequest.url);

      try {
        // authContextRef.handleTokenExpiry() is expected to show a modal and then
        // reject a promise to indicate the user needs to re-authenticate.
        await authContextRef.handleTokenExpiry(); 

        // If handleTokenExpiry were to resolve (meaning silent re-auth happened, which it doesn't currently),
        // then retrying the original request might make sense.
        // However, since it rejects, the catch block below will be executed.
        // For safety, if it somehow didn't reject, we'd still want to reject the original error
        // as the modal flow should take precedence.
        console.warn("axiosConfig: handleTokenExpiry completed unexpectedly without rejection. Rejecting original error.");
        return Promise.reject(error);
      } catch (refreshError) {
        console.error("axiosConfig: AuthContext.handleTokenExpiry indicated failure (e.g., modal shown, user needs to login).", refreshError);
        // After the modal path, we reject the original error. The user's interaction with the modal
        // should lead to a new state (e.g., page reload on successful login).
        return Promise.reject(error);
      }
    }
  );
}; 